import { AppVariant, cx, useTranslations } from "@jugl-web/utils";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { ReactNode, useMemo } from "react";
import { MobileDrawer } from "../../overlay/MobileDrawer";
import {
  DeprecatedButton,
  DeprecatedButtonProps,
} from "../../primitives/DeprecatedButton";
import { Dialog } from "../../primitives/Dialog";
import { PlainButton } from "../../primitives/PlainButton";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import alertAnimation from "./assets/alert.json";

export interface ConfirmationPopupButtonProps
  extends Pick<
    DeprecatedButtonProps,
    "variant" | "color" | "isDisabled" | "onClick"
  > {
  label: string;
}

type ConfirmationPopupWidth = "lg" | "xl";

export interface ConfirmationPopupProps {
  width?: ConfirmationPopupWidth;
  variant: AppVariant;
  isOpen: boolean;
  title: string;
  message: ReactNode;
  buttons: ConfirmationPopupButtonProps[];
  icon?: ReactNode | "alert";
  hasCancelButton?: boolean;
  hasCloseButton?: boolean;
  className?: string;
  onRequestClose: () => void;
}

const sizeMap: Record<ConfirmationPopupWidth, string> = {
  lg: "max-w-lg px-14 pt-10 pb-8",
  xl: "max-w-xl px-28 pt-14 pb-10",
};

export const ConfirmationPopup = ({
  variant,
  isOpen,
  title,
  message,
  buttons,
  icon,
  hasCancelButton,
  hasCloseButton = true,
  className,
  onRequestClose,
  width = "xl",
}: ConfirmationPopupProps) => {
  const { t } = useTranslations();

  const $iconComponent = useMemo(() => {
    if (icon === "alert") {
      return (
        <Lottie
          options={{
            animationData: alertAnimation,
          }}
          width="100%"
          height={70}
        />
      );
    }
    return icon;
  }, [icon]);

  if (variant === "mobile") {
    return (
      <MobileDrawer
        isOpen={isOpen}
        header={{ title }}
        hasBackdrop
        size="auto"
        onClose={onRequestClose}
      >
        <div className="px-4 pb-8">
          {icon && <div className="mb-4">{$iconComponent}</div>}
          <p className="mt-0 mb-12 text-sm text-[#363636]">{message}</p>
          <div className="flex gap-4">
            {hasCancelButton && (
              <DeprecatedButton
                variant="contained"
                color="grey"
                fullWidth
                onClick={onRequestClose}
              >
                Cancel
              </DeprecatedButton>
            )}
            {buttons.map(({ label, ...buttonProps }, index) => (
              <DeprecatedButton key={+index} fullWidth {...buttonProps}>
                {label}
              </DeprecatedButton>
            ))}
          </div>
        </div>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className={cx("text-center", sizeMap[width], className)}
    >
      {hasCloseButton && (
        <PlainButton
          onClick={onRequestClose}
          className="absolute right-4 top-4"
        >
          <CloseIcon />
        </PlainButton>
      )}
      {icon && <div className="mb-4">{$iconComponent}</div>}
      <h3 className="text-dark font-secondary m-0 mb-4 text-lg font-medium leading-[22px]">
        {title}
      </h3>
      <p className="text-grey font-secondary mt-0 mb-14 text-sm leading-3">
        {message}
      </p>
      <div className="flex w-full items-center gap-3">
        {hasCancelButton && (
          <DeprecatedButton
            className="flex-1"
            color="grey"
            variant="contained"
            onClick={onRequestClose}
          >
            {t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            })}
          </DeprecatedButton>
        )}
        {buttons.map(({ label, ...buttonProps }, index) => (
          <DeprecatedButton key={+index} className="flex-1" {...buttonProps}>
            {label}
          </DeprecatedButton>
        ))}
      </div>
    </Dialog>
  );
};
